import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import FEATURE_DATA from '../data/featureData';
import HERO_FEATURE_DATA from '../data/heroFeatureData';
import DemoBookingModal from './DemoBookingModal';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b71b88a0-de31-4e82-b047-c99df2477168/artifacts/7dlw52ts_logo%20%281%29.jpg';
const HERO_BG = 'https://customer-assets.emergentagent.com/job_7fc4408d-deea-4514-b797-0a32a1d11f55/artifacts/ch7afdat_Gemini_Generated_Image_ylp2j1ylp2j1ylp2.png';

export default function FeatureGatePage() {
  const { language } = useLanguage();
  const location = useLocation();
  const featureId = location.pathname.replace('/', '');
  const heroFeature = HERO_FEATURE_DATA[featureId];
  const basicFeature = FEATURE_DATA[featureId];
  const [showDemo, setShowDemo] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const t = (en, hi) => language === 'hi' ? hi : en;

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [featureId]);

  // Use hero data if available, otherwise fall back to basic
  if (heroFeature) return <RichFeaturePage feature={heroFeature} featureId={featureId} showDemo={showDemo} setShowDemo={setShowDemo} t={t} language={language} scrollY={scrollY} />;
  if (basicFeature) return <SimpleFeaturePage feature={basicFeature} featureId={featureId} showDemo={showDemo} setShowDemo={setShowDemo} t={t} language={language} />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="text-center">
        <p className="text-6xl mb-4">🔍</p>
        <h2 className="text-2xl font-bold text-white mb-2">Feature Not Found</h2>
        <Link to="/" className="text-orange-400 hover:underline">Go Home</Link>
      </div>
    </div>
  );
}

/* ===== RICH FEATURE PAGE — for hero features ===== */
function RichFeaturePage({ feature, featureId, showDemo, setShowDemo, t, language, scrollY }) {
  const allHeroFeatures = Object.entries(HERO_FEATURE_DATA).filter(([id]) => id !== featureId);

  return (
    <div className="min-h-screen bg-slate-950 text-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={`feature-rich-${featureId}`}>

      {/* Navbar */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrollY > 50 ? 'bg-slate-950/95 backdrop-blur-md shadow-xl' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Logo" className="h-9 w-9 rounded-lg" />
            <span className="text-lg font-bold">शिक्षा मित्र</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/features" className="hidden md:block px-3 py-1.5 text-sm text-white/60 hover:text-white transition">All Features</Link>
            <Link to="/login" className="px-4 py-2 text-sm font-medium text-white/80 hover:text-white transition border border-white/20 rounded-full" data-testid="gate-login">Login</Link>
            <button onClick={() => setShowDemo(true)} className="px-5 py-2 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition shadow-lg shadow-orange-500/20" data-testid="gate-demo">Free Demo</button>
          </div>
        </div>
      </nav>

      {/* Hero Section — Dark with background image */}
      <section className="relative pt-20 overflow-hidden" data-testid="gate-hero">
        <div className="absolute inset-0 z-0" style={{ backgroundImage: `url('${HERO_BG}')`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-slate-950/90 via-slate-950/80 to-slate-950" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-sm font-medium mb-6">
            <span className="text-2xl">{feature.icon}</span>
            <span className={feature.accentColor}>{feature.title[language]}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-6xl font-black mb-6 tracking-tight leading-tight" style={{ fontFamily: 'Outfit, sans-serif', textShadow: '0 4px 30px rgba(0,0,0,0.5)' }}>
            {feature.tagline[language]}
          </h1>

          <p className="text-base md:text-xl text-white/70 mb-10 max-w-3xl mx-auto leading-relaxed">
            {feature.desc[language]}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button onClick={() => setShowDemo(true)} className="px-8 py-4 bg-orange-500 text-white rounded-full font-bold text-lg hover:bg-orange-600 transition-all shadow-xl shadow-orange-500/30 transform hover:scale-105 hover:-translate-y-1" data-testid="gate-hero-demo">
              {t('Get Free Demo', 'Free Demo लें')} →
            </button>
            <Link to="/login" className="px-8 py-4 bg-white/10 backdrop-blur-md border border-white/30 text-white rounded-full font-semibold text-lg hover:bg-white/20 transition">
              {t('Already have account? Login', 'Account है? Login करें')}
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {feature.stats.map((s, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                <p className="text-2xl md:text-3xl font-black text-orange-400">{s.value}</p>
                <p className="text-xs text-white/50 mt-1">{s.label[language]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 px-4 bg-red-950/30" data-testid="gate-problem">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-red-500/20 text-red-400 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider border border-red-500/30">
              {t('The Problem', 'समस्या')}
            </span>
            <h2 className="text-2xl md:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {feature.problem.title[language]}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {feature.problem.points.map((p, i) => (
              <div key={i} className="flex items-start gap-4 bg-red-500/5 border border-red-500/10 rounded-xl p-5 hover:bg-red-500/10 transition">
                <span className="text-red-400 text-xl mt-0.5">✗</span>
                <p className="text-white/80 leading-relaxed">{p[language]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 px-4" data-testid="gate-solution">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className={`inline-block px-4 py-2 bg-gradient-to-r ${feature.color} text-white rounded-full text-sm font-semibold mb-4 uppercase tracking-wider`}>
              {t('The Solution', 'समाधान')}
            </span>
            <h2 className="text-2xl md:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {feature.solution.title[language]}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {feature.solution.points.map((s, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition group">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">{s.icon}</span>
                  <h3 className="text-lg font-bold">{s.title[language]}</h3>
                </div>
                <p className="text-white/60 leading-relaxed text-sm">{s.desc[language]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Start */}
      <section className={`py-16 px-4 bg-gradient-to-r ${feature.color}`} data-testid="gate-how">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-black mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('Start in 3 Simple Steps', '3 Simple Steps में शुरू करें')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: '📝', title: t('Book Free Demo', 'Free Demo Book करें'), desc: t('Fill name & WhatsApp number. Get demo credentials instantly.', 'Name और WhatsApp number भरें। तुरंत demo credentials पाएं।') },
              { step: '02', icon: '🔐', title: t('Login', 'Login करें'), desc: t('Use your credentials to access the complete school dashboard.', 'Credentials से पूरा school dashboard access करें।') },
              { step: '03', icon: '🚀', title: t('Start Using', 'Use करना शुरू'), desc: t(`${feature.title.en} is ready. Start managing your school.`, `${feature.title.hi} ready है। School manage करना शुरू।`) },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <span className="text-4xl font-black text-white/20 block mb-2">{s.step}</span>
                <span className="text-3xl block mb-3">{s.icon}</span>
                <h3 className="font-bold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-white/70">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Features */}
      <section className="py-16 px-4" data-testid="gate-others">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('Explore More Features', 'और Features देखें')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {allHeroFeatures.map(([id, f]) => (
              <Link key={id} to={`/${id}`} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition group">
                <span className="text-2xl block mb-1">{f.icon}</span>
                <p className="text-xs font-medium text-white/60 group-hover:text-white transition">{f.title[language]}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 text-center" data-testid="gate-final-cta">
        <div className="max-w-2xl mx-auto">
          <span className="text-5xl block mb-6">{feature.icon}</span>
          <h2 className="text-2xl md:text-4xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t(`Ready to use ${feature.title.en}?`, `${feature.title.hi} use करने के लिए ready?`)}
          </h2>
          <p className="text-white/50 mb-8">
            {t('Free demo — credentials on WhatsApp in 2 minutes!', 'Free demo — 2 minute में WhatsApp पर credentials!')}
          </p>
          <button onClick={() => setShowDemo(true)} className="px-10 py-4 bg-orange-500 text-white rounded-full font-bold text-lg hover:bg-orange-600 transition-all shadow-xl shadow-orange-500/30 transform hover:scale-105" data-testid="gate-final-demo">
            {t('Book Free Demo', 'Free Demo Book करें')} →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-lg" />
          <span className="font-bold">शिक्षा मित्र</span>
        </div>
        <p className="text-white/30 text-sm">AI School Management Platform — System Of Success</p>
        <div className="flex justify-center gap-4 mt-4 text-sm text-white/30">
          <Link to="/privacy" className="hover:text-white">Privacy</Link>
          <Link to="/terms" className="hover:text-white">Terms</Link>
          <Link to="/contact" className="hover:text-white">Contact</Link>
        </div>
      </footer>

      <DemoBookingModal isOpen={showDemo} onClose={() => setShowDemo(false)} source={`feature_${featureId}`} />
    </div>
  );
}

/* ===== SIMPLE FEATURE PAGE — for non-hero features ===== */
function SimpleFeaturePage({ feature, featureId, showDemo, setShowDemo, t, language }) {
  const allFeatures = Object.entries(FEATURE_DATA).filter(([id]) => id !== featureId && !id.startsWith('portal-')).slice(0, 6);

  return (
    <div className="min-h-screen bg-slate-950 text-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={`feature-simple-${featureId}`}>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-white/10 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Logo" className="h-9 w-9 rounded-lg" />
            <span className="text-lg font-bold">शिक्षा मित्र</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="px-4 py-2 text-sm text-white/60 hover:text-white transition" data-testid="gate-login">Login</Link>
            <button onClick={() => setShowDemo(true)} className="px-5 py-2 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition" data-testid="gate-demo">Free Demo</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className={`py-20 md:py-28 px-4 bg-gradient-to-br ${feature.color}`}>
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-6xl mb-6 block">{feature.icon}</span>
          <h1 className="text-3xl md:text-5xl font-black mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{feature.title[language]}</h1>
          <p className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto leading-relaxed mb-8">{feature.desc[language]}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => setShowDemo(true)} className="px-8 py-4 bg-white text-gray-800 rounded-full font-bold text-lg hover:bg-gray-100 transition shadow-xl">
              {t('Get Free Demo', 'Free Demo लें')} →
            </button>
            <Link to="/login" className="px-8 py-4 bg-white/20 border border-white/30 text-white rounded-full font-semibold text-lg hover:bg-white/30 transition">
              {t('Login', 'Login करें')}
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('What You Get', 'आपको क्या मिलेगा')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {feature.benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-5">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${feature.color} flex items-center justify-center text-white text-lg font-bold flex-shrink-0`}>{i + 1}</div>
                <p className="text-white/70 leading-relaxed">{b[language]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Features */}
      <section className="py-12 px-4 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-lg font-bold text-center mb-6">{t('Explore More', 'और देखें')}</h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {allFeatures.map(([id, f]) => (
              <Link key={id} to={`/${id}`} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center hover:bg-white/10 transition">
                <span className="text-2xl block mb-1">{f.icon}</span>
                <p className="text-xs text-white/50">{f.title[language]}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 text-center">
        <button onClick={() => setShowDemo(true)} className="px-10 py-4 bg-orange-500 text-white rounded-full font-bold text-lg hover:bg-orange-600 transition shadow-xl shadow-orange-500/30">
          {t('Book Free Demo', 'Free Demo Book करें')} →
        </button>
      </section>

      <footer className="border-t border-white/10 py-6 text-center text-white/30 text-sm">
        <p>शिक्षा मित्र — System Of Success</p>
      </footer>

      <DemoBookingModal isOpen={showDemo} onClose={() => setShowDemo(false)} source={`feature_${featureId}`} />
    </div>
  );
}
